import AddIcon from '@mui/icons-material/Add';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { navigate } from 'hookrouter';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, darken, Stack } from '@mui/material';
import { AppRoutes } from '../constants/AppRoutes';
import { Dialogs } from '../constants/Dialogs';
import { openDialog, setForm } from '../ducks/dialogSlice';
import { fetchGroups } from '../ducks/groups';
import { Group as GroupType } from '../types/Group';
import { stringToLowerIncludes } from '../utils/stringUtils';
import { I18nKeys } from '../constants/I18nKeys';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ConfiguratorFormType } from '../constants/Configurator';
import { Page } from './library/Page';
import { Fab } from './library/Fab';
import { isIdeaRoomGroup, isIdeaRoomUser } from '../utils/userUtils';
import { config } from '../config/config';
import { getDefaultColumnDefinition } from '../utils/MUIDataGridUtils';
import { MUIDataGrid, MUIDataGridColumn } from '../constants/MUIDataGrid';
import { MuiDataGrid } from './MUIDataGrid';
import { PageContentCard } from './library/PageContentCard';
import { PageHeader } from './library/PageHeader';
import { Tabs } from './library/Tabs';
import { Group } from './Group';
import { setMemberDialog } from '../ducks/group';

export enum GroupsComponentTabs {
  Members = 'members',
  Configurators = 'configurators',
}

export const Groups: React.FC<{ groupId?: string }> = ({ groupId: selectedGroupId }) => {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState(GroupsComponentTabs.Members);

  const dispatch = useAppDispatch();
  const apiRef = useGridApiRef();

  const { groups, loading } = useAppSelector((state) => state.groups);
  const searchTerm = useAppSelector((state) => state.search.searchTerm);
  const user = useAppSelector((state) => state.currentUser.user);
  const showClientCreation = useMemo(() => config.environment.STAGE === 'production' && isIdeaRoomUser(user), [user]);

  const [filteredGroups, setFilteredGroups] = useState<GroupType[]>(groups);

  // Effect that filters groups by searchTerm anytime either groups or searchTerm change
  useEffect(() => {
    if (searchTerm.length === 0) {
      setFilteredGroups(groups);
      return;
    }
    const tests = [(group: GroupType): boolean => stringToLowerIncludes(group.groupName, searchTerm)];

    setFilteredGroups(groups.filter((group) => tests.some((test) => test(group))));
  }, [groups, searchTerm]);

  useEffect(() => {
    dispatch(fetchGroups());
  }, [dispatch]);

  const columns: GridColDef[] = useMemo(
    () =>
      [MUIDataGridColumn.GroupName, MUIDataGridColumn.Actions].map((column: MUIDataGridColumn) =>
        getDefaultColumnDefinition(
          { table: MUIDataGrid.Groups, editable: false, resizable: false },
          column,
          {},
          dispatch,
          t,
        ),
      ),
    [t, dispatch],
  );

  const tabs = useMemo(
    () =>
      selectedGroupId
        ? [
            {
              label: t(I18nKeys.GroupMembersTab),
              value: GroupsComponentTabs.Members,
              id: 'members-tab',
              ideaRoomOnly: false,
            },
            {
              label: t(I18nKeys.GroupConfiguratorsTab),
              value: GroupsComponentTabs.Configurators,
              id: 'configurators-tab',
              ideaRoomOnly: false,
            },
          ]
        : [],
    [selectedGroupId, t],
  );

  return (
    <Page sx={{ height: '100%', width: '100%' }}>
      <PageHeader
        id="groups-header"
        header="Groups"
        section={groups.find(({ groupId }) => groupId === selectedGroupId)?.groupName}
        count={!selectedGroupId ? filteredGroups.length : undefined}
        onHeaderClick={() => navigate(AppRoutes.Groups)}
      />
      <PageContentCard sx={{ p: '0px', height: '100%', width: '100%' }}>
        <Stack sx={{ height: '100%', width: '100%' }}>
          <Box>
            <Collapse in={!!selectedGroupId && !isIdeaRoomGroup(selectedGroupId)}>
              <Tabs
                tabs={tabs}
                value={tab}
                onChange={(_e: React.SyntheticEvent<Element, Event>, newValue: GroupsComponentTabs): void => {
                  setTab(newValue);
                }}
                TabIndicatorProps={{ sx: { display: 'none' } }}
                slotProps={{ appBar: { sx: { backgroundColor: darken('#F2F4F5', 0.1) } } }}
              />
            </Collapse>
          </Box>
          {!selectedGroupId && (
            <Box sx={{ height: '100%', width: '100%' }}>
              <MuiDataGrid
                header="Groups"
                loading={loading}
                rows={filteredGroups}
                columns={columns}
                apiRef={apiRef}
                getRowId={({ groupId }) => groupId}
                onRowClick={({ row: { groupId } }) => navigate(`${AppRoutes.Groups}/${groupId}`)}
                showToolbar={false}
              />
            </Box>
          )}
          {selectedGroupId && <Group groupId={selectedGroupId} tab={tab} />}
        </Stack>
      </PageContentCard>

      <Fab
        id="groups-add-fab"
        icon={<AddIcon />}
        label={I18nKeys.AddButton}
        onClick={() => {
          if (showClientCreation) return;
          if (!selectedGroupId) {
            dispatch(setForm(ConfiguratorFormType.AddGroup));
            dispatch(openDialog({ dialog: Dialogs.Configurator }));
            return;
          }
          switch (tab) {
            case GroupsComponentTabs.Configurators:
              dispatch(setForm(ConfiguratorFormType.AddExistingConfigurator));
              dispatch(openDialog({ dialog: Dialogs.Configurator }));
              break;
            case GroupsComponentTabs.Members:
              dispatch(setMemberDialog());
              dispatch(openDialog({ dialog: Dialogs.User }));
              break;
            default:
              break;
          }
        }}
        actions={[
          ...(showClientCreation && !selectedGroupId
            ? [
                {
                  id: 'groups-create-group-with-default-fab',
                  icon: <AddBusinessIcon />,
                  label: t(I18nKeys.ConfiguratorDialogFabCreateGroupWithDefault),
                  onClick: () => {
                    dispatch(setForm(ConfiguratorFormType.AddGroupWithDefault));
                    dispatch(openDialog({ dialog: Dialogs.Configurator }));
                  },
                },
                {
                  id: 'groups-create-group-fab',
                  icon: <AddIcon />,
                  label: t(I18nKeys.ConfiguratorDialogFabCreateGroup),
                  onClick: () => {
                    dispatch(setForm(ConfiguratorFormType.AddGroup));
                    dispatch(openDialog({ dialog: Dialogs.Configurator }));
                  },
                },
              ]
            : []),
          ...(showClientCreation && selectedGroupId
            ? [
                {
                  id: 'groups-add-new-configurator-fab',
                  icon: <AddBusinessIcon />,
                  label: t(I18nKeys.ConfiguratorDialogFabAddNewConfigurator),
                  onClick: () => {
                    dispatch(setForm(ConfiguratorFormType.AddNewConfigurator));
                    dispatch(openDialog({ dialog: Dialogs.Configurator }));
                  },
                },
                {
                  id: 'groups-add-existing-configurator-fab',
                  icon: <AddIcon />,
                  label: t(I18nKeys.ConfiguratorDialogFabAddExistingConfigurator),
                  onClick: () => {
                    dispatch(setForm(ConfiguratorFormType.AddExistingConfigurator));
                    dispatch(openDialog({ dialog: Dialogs.Configurator }));
                  },
                },
              ]
            : []),
        ]}
      />
    </Page>
  );
};
